import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ExternalLink, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DviAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_PILL = {
    draft: "bg-zinc-100 text-zinc-700",
    in_progress: "bg-sky-100 text-sky-800",
    completed: "bg-indigo-100 text-indigo-800",
    customer_review: "bg-violet-100 text-violet-800",
    customer_approved: "bg-emerald-100 text-emerald-800",
    customer_rejected: "bg-rose-100 text-rose-700",
};

export function JobInspectionPanel({ jobId }) {
    const { permissions } = useAuth();
    const canWrite = permissions.includes("dvi.inspection.write");
    const [loading, setLoading] = useState(true);
    const [inspections, setInspections] = useState([]);

    useEffect(() => {
        let alive = true;
        DviAPI.listForJobcard(jobId)
            .then((r) => { if (alive) setInspections(r.items || []); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [jobId]);

    if (loading) {
        return (
            <Card className="p-6">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" />
            </Card>
        );
    }

    const current = inspections.find(
        (i) => !["customer_approved", "customer_rejected"].includes(i.status),
    ) || inspections[0];

    return (
        <Card className="p-4" data-testid="job-inspection-panel">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-tight text-zinc-500">
                        <ClipboardCheck className="h-3.5 w-3.5" />
                        Digital Vehicle Inspection
                    </h3>
                    {current ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span
                                data-testid="job-inspection-status"
                                className={
                                    "rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                                    (STATUS_PILL[current.status] || STATUS_PILL.draft)
                                }
                            >
                                {(current.status || "draft").replace(/_/g, " ")}
                            </span>
                            <div className="flex items-center gap-1">
                                {current.overall_summary?.green_count > 0 && (
                                    <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                        ✓ {current.overall_summary.green_count}
                                    </span>
                                )}
                                {current.overall_summary?.yellow_count > 0 && (
                                    <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                        ! {current.overall_summary.yellow_count}
                                    </span>
                                )}
                                {current.overall_summary?.red_count > 0 && (
                                    <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                        ✕ {current.overall_summary.red_count}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="mt-1.5 text-sm text-zinc-500">
                            No inspection started yet.
                        </p>
                    )}
                </div>
                <Button asChild size="sm" variant="default" data-testid="job-open-inspection">
                    <Link to={`/jobcards/${jobId}/inspection`}>
                        {current ? "Open inspection" : canWrite ? "Start inspection" : "View"}
                        <ExternalLink className="ml-1.5 h-3 w-3" />
                    </Link>
                </Button>
            </div>
            {inspections.length > 1 ? (
                <div className="mt-3 text-xs text-zinc-500">
                    {inspections.length} inspections on this job card.
                </div>
            ) : null}
        </Card>
    );
}
